import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { errorMessage, isAborted } from "./apiError";

/**
 * Hook lấy dữ liệu dùng chung — thay slice Redux cho dữ liệu của TỪNG MÀN.
 *
 * Mỗi màn danh sách trong CMS đều cần đúng bốn thứ: `data / loading / error` +
 * gọi lại sau khi ghi. Trước đây mỗi thứ đó tốn một slice (`createSlice` +
 * `extraReducers` cho pending/fulfilled/rejected) cộng một `createAsyncThunk`,
 * tổng cộng ~60 dòng cho một danh sách. Ở đây là một dòng.
 *
 * Điểm quan trọng hơn cả việc ngắn: **hủy request cũ khi tham số đổi**. Ô tìm
 * kiếm gõ "abc" bắn 3 request; không hủy thì phản hồi của "ab" có thể về sau
 * phản hồi của "abc" và ghi đè lên — người dùng thấy kết quả của từ khóa cũ. Redux
 * không giải bài này, nên một số màn phải tự dựng `requestId` ref (xem
 * `ProductCodeManager`), còn phần lớn màn thì đang có bug đó.
 *
 * @template T
 * @param {(signal: AbortSignal) => Promise<T>} fetcher
 *   Nhận `signal` — truyền xuống axios (`http.get(url, { signal })`) để request cũ
 *   bị hủy thật, không chỉ bị bỏ qua kết quả.
 * @param {unknown[]} deps Đổi là gọi lại, giống `useEffect`.
 * @param {{ enabled?: boolean, initialData?: T }} [options]
 *   enabled: false thì chưa gọi (chờ điều kiện, ví dụ chưa có id).
 * @returns {{ data: T|undefined, loading: boolean, error: string|null, reload: () => void, setData: Function }}
 */
export function useAsync(fetcher, deps, options = {}) {
  const { enabled = true, initialData } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  // Giữ fetcher trong ref: nơi gọi hầu như luôn truyền arrow function mới mỗi lần
  // render, để nó vào deps của useEffect là fetch vô hạn.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let active = true;

    setLoading(true);
    setError(null);

    fetcherRef
      .current(controller.signal)
      .then((result) => {
        if (active) setData(result);
      })
      .catch((e) => {
        if (!active || isAborted(e)) return;
        setError(errorMessage(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, enabled]);

  /** Gọi lại sau khi tạo / sửa / xóa. */
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, reload, setData };
}

/**
 * Hook cho hành động ghi (tạo / sửa / xóa) — nửa còn lại của việc rút Redux.
 *
 * Thay cho khuôn `store.dispatch(thunk(payload))` + `thunk.fulfilled.match(res)`
 * đang lặp ở 130 chỗ. Khuôn đó có hai vấn đề: (1) lỗi bị `rejectWithValue` bọc lại
 * nên nơi gọi phải tự lần vào `res.payload?.message`; (2) không có cờ loading nào
 * nên mỗi màn tự khai `useState` riêng, và nhiều màn quên chặn double-submit.
 *
 * @example
 * const save = useAction(createCustomer, {
 *   successMessage: "Đã thêm khách hàng",
 *   onSuccess: () => { list.reload(); onClose(); },
 * });
 * <Button loading={save.loading} onClick={() => save.run(values)}>Lưu</Button>
 *
 * @param {(...args: any[]) => Promise<any>} action
 * @param {{ successMessage?: string, onSuccess?: Function, onError?: Function, form?: object }} [options]
 *   form: truyền antd form instance để lỗi 422 tự tô đỏ đúng ô.
 * @returns {{ run: (...args) => Promise<any|undefined>, loading: boolean }}
 *   `run` trả về kết quả khi thành công, `undefined` khi thất bại — nơi gọi không
 *   cần try/catch, chỉ cần kiểm tra có giá trị hay không.
 */
export function useAction(action, options = {}) {
  const { successMessage, onSuccess, onError, form } = options;
  const [loading, setLoading] = useState(false);

  // Giữ trong ref để `run` ổn định, truyền xuống component con không gây re-render.
  const ref = useRef({ action, successMessage, onSuccess, onError, form });
  ref.current = { action, successMessage, onSuccess, onError, form };

  /**
   * Cờ chặn double-submit phải là ref, không phải state `loading`: hai lần bấm
   * trong cùng một tick đều đọc `loading === false` (setState chưa kịp áp dụng) nên
   * vẫn tạo hai bản ghi. Ref cập nhật ngay, chặn được.
   */
  const inFlight = useRef(false);

  const run = useCallback(async (...args) => {
    if (inFlight.current) return undefined;
    inFlight.current = true;
    setLoading(true);
    try {
      const result = await ref.current.action(...args);
      const { successMessage: msg, onSuccess: ok } = ref.current;
      if (msg) message.success(msg);
      ok?.(result);
      return result;
    } catch (e) {
      if (isAborted(e)) return undefined;
      const { onError: fail, form: formInstance } = ref.current;
      applyFieldErrors(formInstance, e);
      if (fail) fail(e);
      else message.error(errorMessage(e));
      return undefined;
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, []);

  return { run, loading };
}

/** Đổ lỗi 422 vào antd Form để tô đỏ đúng ô, thay vì chỉ một toast chung. */
function applyFieldErrors(form, error) {
  if (!form?.setFields) return;
  const errors = error?.response?.data?.errors;
  if (!errors || typeof errors !== "object") return;
  form.setFields(
    Object.entries(errors).map(([name, value]) => ({
      name,
      errors: [Array.isArray(value) ? value[0] : String(value)],
    }))
  );
}
